import { readFromLocalStorage } from '@/lib/local-storage/local-storage';
import { useLocalStorage } from 'usehooks-ts';

import { z } from 'zod/v4';

export const AuthDataSchema = z.object({
  token: z.string(),
  user_id: z.string(),
  organization_id: z.string(),
  meta: z.object({
    lifetime_end: z.string(),
    created_at: z.string(),
    ttl: z.number(),
  }),
  customer_id: z.string(),
});

export const BrandingImageSchema = z.object({
  key: z.string(),
  key_prefix: z.string(),
  key_suffix: z.string(),
  md5_hash: z.string(),
  processed: z.boolean(),
  signed_url_get: z.string().optional(),
  signed_url_post: z
    .object({
      fields: z.record(z.string(), z.string()),
      url: z.string(),
    })
    .optional(),
});

export const BrandingSchema = z.object({
  branding: z
    .array(
      z.object({
        name: z.string(),
        domain: z.string(),
        images: z.record(z.string(), BrandingImageSchema).optional(),
        colors: z.record(z.string(), z.string()).optional(),
        texts: z.null(),
        links: z.record(z.string(), z.string()),
        google_tag_id: z.null(),
      })
    )
    .optional(),
  customer_id: z.uuid(),
});

export type AuthData = z.infer<typeof AuthDataSchema>;
export type Branding = z.infer<typeof BrandingSchema>;

export type CurrentBranding = {
  branding: NonNullable<Branding['branding']>[number] | undefined;
  customer_id: Branding['customer_id'];
};

export const BRANDING_DATA_KEY = 'branding' as const;

export function useCurrentBranding() {
  const branding = useLocalStorage<CurrentBranding | null>(
    BRANDING_DATA_KEY,
    null
  );
  return branding;
}

export function getCurrentBranding(): CurrentBranding | null {
  return readFromLocalStorage<CurrentBranding | null>({
    key: BRANDING_DATA_KEY,
    defaultValue: null,
  });
}
